/** GxSeatHandoff (screens §2, §P6 Shared laptop): the named officer's 4-digit PIN lets this device act as them for 10 minutes. */
import { useState } from "react";
import type { ErrorBody } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { clockTime } from "../format";

export interface Handoff { readonly role: "ceo" | "cro"; readonly expiresAt: string }

/** SEAT_PIN_INVALID covers a wrong PIN and the lockout; only the message tells them apart. */
export function pinErrorText(e: ErrorBody["error"], now: Date = new Date()): string {
  if (!/lock/i.test(e.message)) return "Wrong PIN";
  const until = new Date(String((e.details as { lockedUntil?: unknown } | undefined)?.lockedUntil));
  if (Number.isNaN(until.getTime())) return "Locked for a few minutes";
  if (until.getUTCFullYear() >= 9999) return "Locked until your facilitator issues a new PIN";
  const minutes = Math.max(1, Math.ceil((until.getTime() - now.getTime()) / 60_000));
  return `Locked for ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

export function GxSeatHandoff({ api, teamId, role, active, facilitated = true, onActive }: {
  readonly api: ApiClient; readonly teamId: string; readonly role: "ceo" | "cro"; readonly active: Handoff | null; /** Only a facilitated game has a facilitator to hold the PIN. */ readonly facilitated?: boolean; readonly onActive: (h: Handoff) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const label = role === "cro" ? "risk officer" : "CEO";
  const who = role === "cro" ? "the risk officer" : "the CEO";
  const submit = async () => {
    const r = await api.call("seatHandoff", { teamId }, { role, pin });
    if (r.ok) { setOpen(false); setPin(""); setError(null); onActive({ role: r.value.role, expiresAt: r.value.expiresAt }); } else setError(pinErrorText(r.error));
  };
  if (active?.role === role) return <span className="gx-small gx-faint" data-testid="decide.handoff.active">Acting as {label} until {clockTime(active.expiresAt)}</span>;
  return open ? (
    <form className="gx-sign-handoff" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <label className="gx-sr" htmlFor={`pin-${role}`}>{label} seat PIN</label>
      <input id={`pin-${role}`} className="gx-field" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} autoComplete="off" value={pin} placeholder="PIN" onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
      <button type="submit" className="gx-btn gx-btn--primary" disabled={pin.length !== 4}>Hand to {label}</button>
      {facilitated ? <span className="gx-small gx-faint" data-testid="decide.handoff.pin">Type the {label} seat PIN; your facilitator can give you a new one.</span> : null}
      {error === null ? null : <span className="gx-small" role="alert" style={{ color: "var(--crit)" }}>{error}</span>}
    </form>
  ) : <button type="button" className="gx-btn gx-btn--ghost" onClick={() => setOpen(true)}>Hand to {who}</button>;
}
